import * as React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowLeft, CheckCircle2, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { Logo, LogoMark } from "@/components/Logo";

export const APP_NAME = import.meta.env.VITE_APP_NAME || "OneCheckUp";

interface AuthLayoutProps {
  /** Form heading, e.g. "Sign in" */
  title: string;
  subtitle?: React.ReactNode;
  /** Above the heading — typically <BackToSignIn /> */
  back?: React.ReactNode;
  /** Brand panel content (large screens only), under the logo */
  panel?: React.ReactNode;
  /** Under the form: secondary links, legal line */
  footer?: React.ReactNode;
  /** A clinic's own sign-in page: the clinic leads the panel and band, OneCheckUp becomes "Powered by" */
  clinic?: string;
  /** The clinic's logo, shown in place of its initial */
  clinicLogo?: string;
  children: React.ReactNode;
}

// The brand surface is the logo's own mint and ink, and stays light in both themes — like printed
// stationery — so the full-colour logo keeps its two tones. Values are fixed on purpose (not theme tokens).
const BRAND = {
  mint: "#EAF6F4",
  teal: "#00A99A",   // logo teal — decorative strokes only, not text
  tealText: "#0B6E66", // teal that passes 4.5:1 on mint, for small text
  ink: "#212B36",    // "CheckUp" in the wordmark (measured from the artwork)
  inkSoft: "#3E4C59",
};

/** The verified symbol — the logo's ring and tick in the same weights, without the badge — drawn large as
 *  the page's one decorative element (the badge would land in the visible corner and read as a stray "1") */
function TickMotif({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 100 100" aria-hidden="true" className={className}>
      <circle cx="50" cy="50" r="44" fill="none" stroke={BRAND.teal} strokeWidth="8" />
      <path d="M32 52L45 65L68 37" fill="none" stroke={BRAND.teal} strokeWidth="9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** The clinic's logo, or its initial on a square tile when it has none (or the logo fails to load) */
export function ClinicMark({ name, size, logo }: { name: string; size: number; logo?: string }) {
  const [logoFailed, setLogoFailed] = React.useState(false);
  if (logo && !logoFailed) {
    return (
      <img src={logo} alt="" onError={() => setLogoFailed(true)} className="flex-shrink-0 object-contain"
        style={{ width: size, height: size }} />
    );
  }
  const initial = (name.replace(/^dr\.?\s+/i, "").match(/[A-Za-z0-9]/)?.[0] ?? "").toUpperCase();
  return (
    <span
      aria-hidden
      className="flex-shrink-0 inline-flex items-center justify-center font-semibold text-white"
      style={{ width: size, height: size, borderRadius: Math.round(size * 0.24), fontSize: Math.round(size * 0.46), background: BRAND.tealText }}
    >
      {initial}
    </span>
  );
}

/** "Powered by OneCheckUp", small — under a clinic's own panel and form */
function PoweredBy({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <p className={cn("inline-flex items-center gap-1.5 text-xs", className)} style={style}>
      Powered by
      <LogoMark className="w-4 h-4" />
      <span className="font-semibold">{APP_NAME}</span>
    </p>
  );
}

/**
 * Shared frame for the signed-out pages (sign in, sign up, forgot and reset password):
 * the brand panel on large screens, a slim brand band on phones, and one narrow form column.
 */
export function AuthLayout({ title, subtitle, back, panel, footer, clinic, clinicLogo, children }: AuthLayoutProps) {
  return (
    <div className="min-h-[100dvh] flex flex-col lg:flex-row bg-card text-foreground">
      {/* Brand panel — large screens */}
      <aside
        className="hidden lg:flex relative overflow-hidden w-[44%] max-w-[600px] flex-col p-10 xl:p-14"
        style={{ background: BRAND.mint, color: BRAND.ink }}
      >
        <TickMotif className="absolute -right-28 -bottom-36 [@media(max-height:700px)]:-bottom-44 w-[400px] h-[400px] opacity-[0.12] pointer-events-none" />
        {clinic ? <span className="relative"><ClinicMark name={clinic} size={64} logo={clinicLogo} /></span> : <Logo size={64} tagline tone="light" className="relative" />}
        <div className={cn("relative my-auto max-w-sm", clinic ? "pb-8" : "pb-16")}>{panel}</div>
        {clinic && <PoweredBy className="relative" style={{ color: BRAND.inkSoft }} />}
      </aside>

      {/* Brand band — phones and tablets */}
      <header className="lg:hidden flex justify-center px-4 py-4" style={{ background: BRAND.mint }}>
        {clinic ? (
          <span className="flex items-center gap-2.5 min-w-0">
            <ClinicMark name={clinic} size={40} logo={clinicLogo} />
            <span className="text-lg font-semibold leading-tight truncate" style={{ color: BRAND.ink }}>{clinic}</span>
          </span>
        ) : <Logo size={48} tone="light" />}
      </header>

      {/* Form column */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 py-10">
        <div className="w-full max-w-[380px]">
          {back && <div className="mb-5">{back}</div>}

          <h1 className="text-2xl font-semibold leading-8 tracking-[-0.01em] text-foreground">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}

          <div className="mt-7">{children}</div>

          {footer && <div className="mt-8 space-y-3 text-center text-sm text-muted-foreground">{footer}</div>}
          {clinic && <div className="lg:hidden mt-6 text-center"><PoweredBy className="text-muted-foreground" /></div>}
        </div>
      </main>
    </div>
  );
}

/** Headline for the brand panel: ink title, the logo's short teal rule, one supporting line */
export function PanelBlock({ eyebrow, title, children }: { eyebrow?: string; title?: React.ReactNode; children?: React.ReactNode }) {
  return (
    <section>
      {eyebrow && <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: BRAND.tealText }}>{eyebrow}</p>}
      {title && <h2 className="mt-2 text-[2.5rem] font-semibold leading-[1.1] tracking-[-0.02em]" style={{ color: BRAND.ink }}>{title}</h2>}
      <div className="my-5 h-0.5 w-12" style={{ background: BRAND.teal }} aria-hidden />
      {children && <div className="text-[15px] leading-relaxed space-y-2" style={{ color: BRAND.inkSoft }}>{children}</div>}
    </section>
  );
}

const NOTICE = {
  error:   { icon: AlertTriangle, cls: "border-destructive/30 bg-destructive/5 text-destructive" },
  warning: { icon: Clock,         cls: "border-highlight-foreground/20 bg-highlight text-highlight-foreground" },
  success: { icon: CheckCircle2,  cls: "border-success/30 bg-success/5 text-success" },
} as const;

/** One-line message inside an auth form: an error, the session-expired note, or a success confirmation. */
export function AuthNotice({ tone, children, action, id }: {
  tone: keyof typeof NOTICE; children: React.ReactNode; action?: React.ReactNode; id?: string;
}) {
  const { icon: Icon, cls } = NOTICE[tone];
  return (
    <div id={id} role={tone === "error" ? "alert" : "status"} className={cn("flex items-start gap-2.5 rounded-lg border px-3 py-2.5 text-sm", cls)}>
      <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden />
      <div className="flex-1 min-w-0">{children}</div>
      {action}
    </div>
  );
}

/** "← Back to sign in", for the `back` slot */
export function BackToSignIn() {
  return (
    <Link to="/login" className="touch-target inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
      <ArrowLeft className="w-4 h-4" aria-hidden />
      Back to sign in
    </Link>
  );
}
